import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const loadScript = (src) => new Promise((resolve, reject) => {
  const existing = document.querySelector(`script[src="${src}"]`);

  if (existing) {
    if (existing.dataset.loaded === 'true') {
      resolve();
      return;
    }

    const handleLoad = () => {
      existing.dataset.loaded = 'true';
      resolve();
      existing.removeEventListener('load', handleLoad);
      existing.removeEventListener('error', handleError);
    };

    const handleError = (event) => {
      reject(event);
      existing.removeEventListener('load', handleLoad);
      existing.removeEventListener('error', handleError);
    };

    existing.addEventListener('load', handleLoad);
    existing.addEventListener('error', handleError);
    return;
  }

  const script = document.createElement('script');
  script.src = src;
  script.async = true;
  script.dataset.loaded = 'false';

  script.onload = () => {
    script.dataset.loaded = 'true';
    resolve();
  };

  script.onerror = reject;
  document.head.appendChild(script);
});

const paletteForMode = (nightMode) => nightMode ? {
  backgroundColor: 0x020108,
  colorPrimary: 0xff6ba9,
  colorSecondary: 0xff99cc
} : {
  backgroundColor: 0xffffff,
  colorPrimary: 0xff6ba9,
  colorSecondary: 0xff7aa6
};

const EducationBackground = ({ nightMode }) => {
  const containerRef = useRef(null);
  const vantaRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const isMobile = window.innerWidth <= 768;

    const initVanta = async () => {
      if (!containerRef.current) return;

      // Disable Vanta entirely on mobile for performance
      if (isMobile) return;

      if (vantaRef.current) {
        vantaRef.current.destroy();
        vantaRef.current = null;
      }

      if (typeof window !== 'undefined') {
        window.THREE = window.THREE || THREE;
      }

      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/p5.js/1.6.0/p5.min.js');

      if (!window.VANTA || !window.VANTA.TOPOLOGY) {
        await loadScript('https://cdn.jsdelivr.net/npm/vanta@0.5.24/dist/vanta.topology.min.js');
      }

      if (cancelled) return;

      if (window.VANTA && window.VANTA.TOPOLOGY) {
        const palette = paletteForMode(nightMode);
        vantaRef.current = window.VANTA.TOPOLOGY({
          el: containerRef.current,
          mouseControls: false,
          touchControls: false,
          gyroControls: false,
          minHeight: 200.0,
          minWidth: 200.0,
          scale: 1.0,
          scaleMobile: 1.0,
          color: palette.colorPrimary,
          color2: palette.colorSecondary,
          backgroundColor: palette.backgroundColor,
          points: 8.0,
          maxDistance: 18.0,
          spacing: 18.0,
          speed: 1.2,
          animationSpeed: 1.2
        });
      }
    };

    initVanta();

    return () => {
      cancelled = true;
      if (vantaRef.current) {
        vantaRef.current.destroy();
        vantaRef.current = null;
      }
    };
  }, [nightMode]);

  return <div className="edu-background" ref={containerRef} />;
};

export default EducationBackground;
